import { useState } from 'react';
import axios from 'axios';
import { ChevronLeft } from 'lucide-react';
import { adminProblemsApi, type CreateProblemRequest, type Difficulty } from '@/lib/api/admin-problems';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProblemMetadataSidebar } from '@/features/AdminProblems/ProblemMetadataSidebar';
import { ProblemDescriptionTab } from '@/features/AdminProblems/ProblemDescriptionTab';
import { ProblemStarterCodeTab } from '@/features/AdminProblems/ProblemStarterCodeTab';
import { ScrollFadeArea } from '@/components/ScrollFadeArea';
import { cn } from '@/lib/cn';
import { PRIMARY_ACTION } from './actionStyles';

interface Props {
  /** Navigation is injected so each route can use its own typed `to`. */
  onBack: () => void;
  onCreated: (id: string) => void;
}

/**
 * The "new problem" form, shared by /admin/problems/new and
 * /trainer/problems/new. The API stamps the caller as the author, so a
 * trainer's problem comes out owned by them and a super_admin's comes out
 * KodPrep-curated — nothing here has to distinguish them.
 */
export function NewProblemForm({ onBack, onCreated }: Props) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CreateProblemRequest>({
    title: '',
    description: '',
    constraints: '',
    examples: [],
    follow_up: '',
    difficulty: 'easy',
    tags: [],
    company_tags: [],
    company_frequency: {},
    time_limit_ms: 2000,
    memory_limit_mb: 256,
    starter_code: {},
  });

  function handleChange(field: string, value: unknown) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      setSaving(true);
      const created = await adminProblemsApi.create(form);
      toast.success('Problem created');
      onCreated(created.id);
    } catch (err) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      toast.error(typeof msg === 'string' && msg ? msg : 'Failed to create problem');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col h-full -m-6">
      <div className="flex items-center justify-between px-5 py-3 border-b bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-2 text-[13px]">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Problems
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold text-foreground">New Problem</span>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            className={cn('text-[13px]', PRIMARY_ACTION)}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Creating…' : 'Save Draft'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-[220px] shrink-0 overflow-y-auto border-r">
          <ProblemMetadataSidebar
            difficulty={form.difficulty as Difficulty}
            tags={form.tags}
            companyTags={form.company_tags}
            companyFrequency={form.company_frequency}
            timeLimitMs={form.time_limit_ms}
            memoryLimitMb={form.memory_limit_mb}
            slug={undefined}
            status="draft"
            onChange={handleChange}
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <Tabs defaultValue="description" className="h-full flex flex-col">
            <TabsList className="mx-4 mt-3 w-fit">
              <TabsTrigger value="description" className="text-[13px]">Description</TabsTrigger>
              <TabsTrigger value="starter" className="text-[13px]">Starter Code</TabsTrigger>
              <TabsTrigger value="testcases" className="text-[13px]" disabled>
                Test Cases <span className="ml-1 text-xs text-muted-foreground">(save first)</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="description" className="relative flex-1 overflow-hidden">
              <ScrollFadeArea className="h-full overflow-y-auto">
                <ProblemDescriptionTab
                  title={form.title}
                  description={form.description}
                  constraints={form.constraints}
                  examples={form.examples}
                  followUp={form.follow_up}
                  onChange={handleChange}
                />
              </ScrollFadeArea>
            </TabsContent>
            <TabsContent value="starter" className="relative flex-1 overflow-hidden">
              <ScrollFadeArea className="h-full overflow-y-auto">
                <ProblemStarterCodeTab
                  starterCode={form.starter_code}
                  onChange={(lang, code) =>
                    handleChange('starter_code', { ...form.starter_code, [lang]: code })
                  }
                />
              </ScrollFadeArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
